/**
 * ManifestsSection - Portless manifests for the packing station
 *
 * Idle-only section mounted after the packing stat-card grid. Two cards:
 *  1. Customs manifest — lets a packer at a Portless/Thread Lab location
 *     generate the next customs manifest (XLSX) for the shipped-but-
 *     unmanifested orders, download it, and re-download recent ones.
 *  2. Portless inbound manifest — manual "Send now" for the Portless email
 *     (the daily auto-send cron was removed; this is the only station-side
 *     trigger — BLU-600 PR2).
 *
 * CROSS-ORG (Thread Lab cutover): BOTH manifests cover exactly ONE client org —
 * client customs/Portless settings (recipients included) are never mixed at a
 * shared 3PL location. getStatus and portlessStatus therefore each return a
 * `clients[]` breakdown, and each card renders ONE block per client with its
 * own counts, its own primary action (which names that client's org id) and its
 * own history. Counts, lists and in-flight spinners are never merged — sending
 * Mini Zali's manifest must not spin Threadheads' button. When more than one
 * client is declarable here — or the single client is not the location owner —
 * every block is headed by the client org name, so a Thread Lab operator always
 * knows whose paperwork they are minting. A single owner-org client renders
 * exactly as the pre-cutover single-tenant cards did (no heading).
 *
 * GATE: renders nothing unless getStatus.applicable === true (card 1's gate;
 * both cards live at the same Thread Lab / Portless location). HQ /
 * StarShipIt packers never see this section.
 *
 * DESIGN: production station language (PRODUCTION_DESIGN.md) — shadcn Card,
 * ≥48px hit targets (h-11 primary), semantic colour, modal-free non-critical
 * feedback via toast. Mirrors PackingPage's Recently-Packed card + the
 * LabelPrintDialog base64→download idiom (print-utils `downloadBlob`).
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { downloadBlob } from '@/lib/print-utils';
import { toast } from 'sonner';
import { getUserMessage } from '@/lib/trpc-error-utils';
import {
  useCustomsManifestStatus,
  useGenerateCustomsManifest,
  useDownloadCustomsManifest,
  type CustomsManifestClient,
} from '../hooks/useCustomsManifest';
import {
  usePortlessStatus,
  useSendPortlessNow,
  type PortlessManifestClient,
} from '../hooks/usePortlessManifest';
import { FileSpreadsheet, Download, Loader2, Clock, Send, Truck } from 'lucide-react';

/** XLSX (OpenXML spreadsheet) MIME for the generated manifest workbook. */
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Decode a base64 payload to an object URL. Mirrors LabelPrintDialog.createBlobUrl. */
function base64ToBlobUrl(base64: string, mime: string): string {
  const byteCharacters = atob(base64);
  const byteNumbers = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([byteNumbers], { type: mime }));
}

/** Download a base64 XLSX payload, revoking the object URL afterwards. */
function downloadManifest(base64: string, filename: string): void {
  const url = base64ToBlobUrl(base64, XLSX_MIME);
  downloadBlob(url, filename);
  URL.revokeObjectURL(url);
}

function formatTimeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * The identity fields both cards' per-client payloads share. Declared here so
 * the ordering + naming rules below are written once and applied identically to
 * the customs and Portless client lists.
 */
type ManifestClientIdentity = {
  organizationId: string;
  organizationName: string;
  isOwner: boolean;
};

/**
 * Owner org first, then alphabetical — a stable block order so an operator
 * acting for the same client every shift always finds it in the same place.
 */
function byOwnerThenName(a: ManifestClientIdentity, b: ManifestClientIdentity): number {
  if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
  return a.organizationName.localeCompare(b.organizationName);
}

/**
 * Pre-cutover single-tenant look: no heading when the only declarable client is
 * the location's own org. Any other shape names every client explicitly — an
 * unlabelled customs sheet (or Portless email) at a shared location is a
 * compliance problem, not a cosmetic one.
 *
 * (`clients[0]` is bound first: noUncheckedIndexedAccess is on, and TS does not
 * narrow an index access from a `.length === 1` check.)
 */
function showClientNamesFor(clients: ManifestClientIdentity[]): boolean {
  const onlyClient = clients.length === 1 ? clients[0] : undefined;
  return clients.length > 1 || (!!onlyClient && !onlyClient.isOwner);
}

/**
 * Monogram + org name, the heading both cards put above a client's block.
 * Monogram is fill-free and differentiates by initial, not hue — warehouse
 * lighting + colour-blindness (PRODUCTION_DESIGN.md §8).
 */
function ClientNameHeading({ name }: { name: string }) {
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-foreground ring-1 ring-border"
        aria-hidden="true"
      >
        {initial}
      </span>
      <h4 className="min-w-0 truncate text-base font-semibold leading-tight">{name}</h4>
    </div>
  );
}

interface ClientManifestBlockProps {
  client: CustomsManifestClient;
  /** Show the org-name heading (multi-client, or a single non-owner client). */
  showClientName: boolean;
  /** This client's generation is in flight. */
  isGenerating: boolean;
  /** ANY client's generation is in flight — one manifest at a time. */
  anyGenerating: boolean;
  downloadingId: string | null;
  onGenerate: (client: CustomsManifestClient) => void;
  onDownload: (manifestId: string) => void;
  className?: string;
}

/**
 * One client org's slice of the customs card: ready-count, generate CTA and
 * recent manifests. Self-contained so two clients can never share a count, a
 * list, or a spinner.
 */
function ClientManifestBlock({
  client,
  showClientName,
  isGenerating,
  anyGenerating,
  downloadingId,
  onGenerate,
  onDownload,
  className,
}: ClientManifestBlockProps) {
  const { t } = useTranslation('production');
  const nothingReady = client.unmanifestedCount === 0;

  return (
    <section
      className={cn('space-y-4', className)}
      aria-label={
        showClientName
          ? t('packer.manifest.clientSection', { client: client.organizationName })
          : undefined
      }
    >
      {showClientName && <ClientNameHeading name={client.organizationName} />}

      {/* Ready count + primary CTA */}
      <div className="space-y-2">
        <p className="text-base">
          {t('packer.manifest.ready', { count: client.unmanifestedCount })}
        </p>
        <Button
          onClick={() => onGenerate(client)}
          disabled={nothingReady || anyGenerating}
          aria-busy={isGenerating}
          aria-label={
            showClientName
              ? t('packer.manifest.generateAria', { client: client.organizationName })
              : undefined
          }
          className="w-full h-11 text-base font-semibold gap-2"
        >
          {isGenerating ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-5 w-5" aria-hidden="true" />
          )}
          {isGenerating
            ? t('packer.manifest.generating')
            : t('packer.manifest.generate')}
        </Button>
        {nothingReady && (
          <p className="text-sm text-muted-foreground">
            {t('packer.manifest.noneReady')}
          </p>
        )}
      </div>

      {/* Recent manifests */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-muted-foreground">
            {t('packer.manifest.recent')}
          </p>
        </div>
        {client.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('packer.manifest.recentEmpty')}
          </p>
        ) : (
          <div className="divide-y rounded-lg border">
            {client.recent.map((manifest) => (
              <div key={manifest.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="font-mono font-medium text-base tabular-nums shrink-0">
                  #{manifest.sequence_no}
                </span>
                <span className="text-sm text-muted-foreground truncate">
                  {t('packer.manifest.orderLineCount', {
                    orders: manifest.order_count,
                    lines: manifest.line_count,
                  })}
                </span>
                <span className="text-xs text-muted-foreground shrink-0 ml-auto w-14 text-right">
                  {formatTimeAgo(manifest.created_at)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 shrink-0 gap-1.5"
                  onClick={() => onDownload(manifest.id)}
                  disabled={downloadingId === manifest.id}
                  aria-label={
                    showClientName
                      ? t('packer.manifest.downloadAriaClient', {
                          sequenceNo: manifest.sequence_no,
                          client: client.organizationName,
                        })
                      : t('packer.manifest.downloadAria', {
                          sequenceNo: manifest.sequence_no,
                        })
                  }
                >
                  {downloadingId === manifest.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t('packer.manifest.download')}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface PortlessClientBlockProps {
  client: PortlessManifestClient;
  /** Show the org-name heading (multi-client, or a single non-owner client). */
  showClientName: boolean;
  /** This client's send is in flight. */
  isSending: boolean;
  /** ANY client's send is in flight — one email at a time. */
  anySending: boolean;
  onSend: (client: PortlessManifestClient) => void;
  className?: string;
}

/**
 * One client org's slice of the Portless card: its own pending counts, its own
 * Send CTA and its own last-sent time. Self-contained so two clients can never
 * share a count, a spinner, or — worst case — a recipient list.
 */
function PortlessClientBlock({
  client,
  showClientName,
  isSending,
  anySending,
  onSend,
  className,
}: PortlessClientBlockProps) {
  const { t } = useTranslation('production');
  const pendingTotal = client.pending.crossdockOrders + client.pending.hybridOrders;
  const nothingPending = pendingTotal === 0;
  // Lines Portless picks from its OWN stock. Called out separately so the pending
  // figure above is never read as "this many units are arriving inbound" — some
  // of them are already on Portless's shelves.
  const portlessStockUnits =
    (client.pending.crossdockPortlessUnits ?? 0) + (client.pending.hybridPortlessUnits ?? 0);

  return (
    <section
      className={cn('space-y-4', className)}
      aria-label={
        showClientName
          ? t('packer.portless.clientSection', { client: client.organizationName })
          : undefined
      }
    >
      {showClientName && <ClientNameHeading name={client.organizationName} />}

      {/* Pending count + primary CTA + last-sent — same rhythm as the customs
          block's count/CTA group, so the two cards read as one surface. */}
      <div className="space-y-2">
        <p className="text-base">
          {nothingPending
            ? t('packer.portless.noneReady')
            : t('packer.portless.pending', {
                crossdock: client.pending.crossdockOrders,
                hybrid: client.pending.hybridOrders,
              })}
        </p>
        <Button
          onClick={() => onSend(client)}
          disabled={nothingPending || anySending}
          aria-busy={isSending}
          aria-label={
            showClientName
              ? t('packer.portless.sendNowAria', { client: client.organizationName })
              : undefined
          }
          className="w-full h-11 text-base font-semibold gap-2"
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="h-5 w-5" aria-hidden="true" />
          )}
          {isSending ? t('packer.portless.sending') : t('packer.portless.sendNow')}
        </Button>
        {/* BELOW the CTA, matching the customs block's conditional line. Above it,
            this shifted the send button's position depending on data — a mis-tap
            risk on a gloved, high-tempo floor (PRODUCTION_DESIGN.md §1). */}
        {!nothingPending && portlessStockUnits > 0 && (
          <p className="text-sm text-muted-foreground">
            {t('packer.portless.pendingPortlessStock', { count: portlessStockUnits })}
          </p>
        )}
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {client.lastSentAt
            ? t('packer.portless.lastSent', { time: formatTimeAgo(client.lastSentAt) })
            : t('packer.portless.neverSent')}
        </p>
      </div>
    </section>
  );
}

interface ManifestsSectionProps {
  locationId: string;
}

export function ManifestsSection({ locationId }: ManifestsSectionProps) {
  const { t } = useTranslation('production');
  const { data } = useCustomsManifestStatus(locationId);
  const generateMutation = useGenerateCustomsManifest();
  const downloadManifestById = useDownloadCustomsManifest();

  const { data: portlessData } = usePortlessStatus(locationId);
  const sendPortlessNow = useSendPortlessNow();

  // Per-row download in-flight tracking (recent list).
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Per-client generate in-flight tracking. generateMutation.isPending is
  // shared across every client's button, so it can't drive the spinner —
  // generating for Mini Zali must never spin Threadheads' button.
  const [generatingOrgId, setGeneratingOrgId] = useState<string | null>(null);
  // Same story on the Portless side: sendPortlessNow.isPending is shared across
  // every client's button, so it can't drive the spinner.
  const [sendingOrgId, setSendingOrgId] = useState<string | null>(null);

  const clients = data?.applicable ? [...data.clients].sort(byOwnerThenName) : [];
  const showClientNames = showClientNamesFor(clients);

  // The two cards resolve their own client lists (customs hides clients with no
  // history AND no ready orders), so each labels according to what IT renders.
  const portlessClients = portlessData?.applicable
    ? [...portlessData.clients].sort(byOwnerThenName)
    : [];
  const showPortlessClientNames = showClientNamesFor(portlessClients);

  const handleGenerate = useCallback(
    async (client: CustomsManifestClient) => {
      setGeneratingOrgId(client.organizationId);
      try {
        const result = await generateMutation.mutateAsync({
          locationId,
          clientOrganizationId: client.organizationId,
        });
        if (!result.generated) {
          toast.info(t('packer.manifest.nothingGenerated'));
          return;
        }
        downloadManifest(result.base64, result.filename);
        toast.success(
          showClientNames
            ? t('packer.manifest.generateSuccessClient', {
                sequenceNo: result.sequenceNo,
                orders: result.orderCount,
                client: client.organizationName,
              })
            : t('packer.manifest.generateSuccess', {
                sequenceNo: result.sequenceNo,
                orders: result.orderCount,
              })
        );
      } catch (error) {
        toast.error(t('packer.manifest.generateFailed'), {
          description: getUserMessage(error),
        });
      } finally {
        setGeneratingOrgId(null);
      }
    },
    [generateMutation, locationId, showClientNames, t]
  );

  const handleDownloadRecent = useCallback(
    async (manifestId: string) => {
      setDownloadingId(manifestId);
      try {
        const result = await downloadManifestById(manifestId);
        downloadManifest(result.base64, result.filename);
      } catch (error) {
        toast.error(t('packer.manifest.downloadFailed'), {
          description: getUserMessage(error),
        });
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadManifestById, t]
  );

  const handleSendPortlessNow = useCallback(
    async (client: PortlessManifestClient) => {
      // Every outcome toast names the client whenever the blocks are labelled —
      // a bare "Could not send to Portless" is unactionable when two clients'
      // buttons sit side by side.
      const failureTitle = showPortlessClientNames
        ? t('packer.portless.sendFailedClient', { client: client.organizationName })
        : t('packer.portless.sendFailed');

      setSendingOrgId(client.organizationId);
      try {
        const result = await sendPortlessNow.mutateAsync({
          locationId,
          clientOrganizationId: client.organizationId,
        });
        const sent: string[] = [];
        if (result.crossdock === 'sent') {
          sent.push(
            t('packer.portless.sentPart', {
              label: t('packer.portless.crossDock'),
              count: result.crossdockCount,
            })
          );
        }
        if (result.hybrid === 'sent') {
          sent.push(
            t('packer.portless.sentPart', {
              label: t('packer.portless.hybrid'),
              count: result.hybridCount,
            })
          );
        }
        // runPortlessExport does NOT throw on a send failure — it returns the
        // partition outcome as 'failed' (e.g. unconfigured recipients / Resend
        // error). Surface that as an error, not the reassuring "nothing to send".
        const failed = result.crossdock === 'failed' || result.hybrid === 'failed';
        // 'skipped' on BOTH partitions is unambiguous: a partition with no lines
        // is reported as 'empty', so 'skipped' means there WAS volume and the
        // send was refused — in practice a missing manifest recipient. Reporting
        // that as "nothing to send" is the silent no-op the operator can never
        // diagnose: the pending counts on the same card stay non-zero while the
        // button appears to have worked.
        const configSkipped = result.crossdock === 'skipped' && result.hybrid === 'skipped';
        if (configSkipped) {
          toast.error(failureTitle, {
            description: t('packer.portless.sendNoRecipient', { client: client.organizationName }),
            duration: Infinity,
          });
        } else if (failed) {
          toast.error(failureTitle, {
            description: sent.length
              ? t('packer.portless.sendPartialFailed', { parts: sent.join(', ') })
              : t('packer.portless.sendFailedDescription'),
          });
        } else if (sent.length) {
          toast.success(
            showPortlessClientNames
              ? t('packer.portless.sendSuccessClient', {
                  client: client.organizationName,
                  parts: sent.join(', '),
                })
              : t('packer.portless.sendSuccess', { parts: sent.join(', ') })
          );
        } else {
          toast.info(
            showPortlessClientNames
              ? t('packer.portless.sendNothingToSendClient', {
                  client: client.organizationName,
                })
              : t('packer.portless.sendNothingToSend')
          );
        }
      } catch (error) {
        toast.error(failureTitle, {
          description: getUserMessage(error),
        });
      } finally {
        setSendingOrgId(null);
      }
    },
    [sendPortlessNow, locationId, showPortlessClientNames, t]
  );

  // Gate: nothing renders until at least one of the two cards has confirmed it
  // belongs here (this also suppresses the initial-load flash). Deliberately NOT
  // gated on the customs status alone — the two queries answer independently
  // (customs additionally hides clients with no ready orders and no history), so
  // gating both on the customs one would let a customs-side mismatch silently
  // take away the packer's Portless "Send now" trigger as collateral.
  if (!data?.applicable && !portlessData?.applicable) return null;

  return (
    <>
      {clients.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet
                className="h-5 w-5 text-muted-foreground"
                aria-hidden="true"
              />
              <CardTitle className="text-lg">{t('packer.manifest.title')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-5">
            {clients.map((client, index) => (
              <ClientManifestBlock
                key={client.organizationId}
                client={client}
                showClientName={showClientNames}
                isGenerating={generatingOrgId === client.organizationId}
                anyGenerating={generatingOrgId !== null}
                downloadingId={downloadingId}
                onGenerate={handleGenerate}
                onDownload={handleDownloadRecent}
                className={index > 0 ? 'border-t pt-5' : undefined}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {portlessClients.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <CardTitle className="text-lg">{t('packer.portless.title')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-5">
            {portlessClients.map((client, index) => (
              <PortlessClientBlock
                key={client.organizationId}
                client={client}
                showClientName={showPortlessClientNames}
                isSending={sendingOrgId === client.organizationId}
                anySending={sendingOrgId !== null}
                onSend={handleSendPortlessNow}
                className={index > 0 ? 'border-t pt-5' : undefined}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}
